/**
 * Cart Bubble Updater - Updates cart count bubble when cart changes
 */
class CartBubbleUpdater {
  static init() {
    this.updateCartBubble();
    this.setupCartUpdateListeners();
  }

  static setupCartUpdateListeners() {
    document.addEventListener('cart:updated', (event) => {
      if (event.detail && event.detail.item_count !== undefined) {
        this.updateCartBubbleWithCount(event.detail.item_count);
      } else {
        this.updateCartBubble();
      }
    });
  }

  static async updateCartBubble() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    if (!response.ok) throw new Error('Failed to fetch cart: ' + response.status);
    const cartData = await response.json();
    this.updateCartBubbleWithCount(cartData.item_count || 0);
  }

  static updateCartBubbleWithCount(count) {
    const cartBubbles = document.querySelectorAll('.cart-count-bubble');
    cartBubbles.forEach(bubble => {
      const visualSpan = bubble.querySelector('span[aria-hidden="true"]');
      const hiddenSpan = bubble.querySelector('span.visually-hidden');
      if (visualSpan) visualSpan.textContent = count.toString();
      if (hiddenSpan) hiddenSpan.textContent = count + ' ' + (count === 1 ? 'item' : 'items');
      if (count > 0) {
        bubble.classList.add('has-items');
        bubble.classList.remove('empty');
      } else {
        bubble.classList.add('empty');
        bubble.classList.remove('has-items');
      }
      if (count > parseInt(visualSpan?.dataset.previousCount || '0')) {
        bubble.classList.add('cart-updated');
        setTimeout(() => bubble.classList.remove('cart-updated'), 300);
      }
      if (visualSpan) visualSpan.dataset.previousCount = count.toString();
    });
  }

}

window.CartBubbleUpdater = CartBubbleUpdater;

class CartDrawer {
  static isOpen = false;
  static currentSlide = 0;
  static isUpdating = false;
  static debounceTimer = null;
  static init() {
    this.bindEvents();
    this.updateShippingProgress();
    this.initSlider();
    this.initVariantHandlers();
    this.setupKeyboardNavigation();
  }

  static open() {
    const overlay = document.getElementById('cartDrawerOverlay');
    const drawer = document.getElementById('cartDrawer');
    if (!overlay || !drawer) {
      return;
    }
    overlay.classList.add('active');
    drawer.classList.add('active');
    document.documentElement.classList.add('overflow-hidden');
    const firstFocusable = drawer.querySelector('button, input, select, [tabindex="0"]');
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 100);
    }
    this.isOpen = true;
  }

  static close() {
    const overlay = document.getElementById('cartDrawerOverlay');
    const drawer = document.getElementById('cartDrawer');
    if (!overlay || !drawer) return;
    overlay.classList.remove('active');
    drawer.classList.remove('active');
    document.documentElement.classList.remove('overflow-hidden');
    this.isOpen = false;
  }

  static toggle() {
    this.isOpen ? this.close() : this.open();
  }

  static setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
    });
  }

  static updateShippingProgress() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => this.updateShippingProgressFromData(cart))
      .catch(error => {});
  }

  static updateShippingProgressFromData(cartData) {
    const cartTotal = cartData.total_price || 0;
    const progressBar = document.getElementById('shippingProgressBar');
    const progressText = document.getElementById('shippingProgressText');
    if (!progressBar || !progressText) return;
    // Threshold comes from the Liquid theme setting (settings.cart_shipping_threshold),
    // rendered onto the progress bar as data-free-shipping-threshold. Cents, e.g. $200 = 20000.
    const threshold = Number(progressBar.dataset.freeShippingThreshold) || 0;
    // Guard against an unset/zero threshold: never show "you qualify" for free.
    if (threshold <= 0) {
      progressBar.style.width = '0%';
      progressBar.classList.remove('complete');
      progressText.classList.remove('complete');
      progressText.innerHTML = '';
      return;
    }
    const percentage = Math.min((cartTotal / threshold) * 100, 100);
    const remaining = threshold - cartTotal;
    progressBar.style.width = percentage + '%';
    if (cartTotal >= threshold) {
      progressBar.classList.add('complete');
      progressText.classList.add('complete');
      progressText.innerHTML = '🎉 You qualify for free shipping!';
    } else {
      progressBar.classList.remove('complete');
      progressText.classList.remove('complete');
      const remainingFormatted = this.formatCurrency(remaining);
      progressText.innerHTML = 'You\'re <span class="shipping-amount">' + remainingFormatted + '</span> away from FREE shipping';
    }
  }

  static bindEvents() {
    document.addEventListener('click', (e) => {
      // Image blocks are a mouse shortcut; the title link is the keyboard-accessible equivalent
      const imageLink = e.target.closest('.cart-item-image[data-href], .slide-product-image[data-href]');
      if (imageLink) {
        window.location.href = imageLink.dataset.href;
        return;
      }
      if (e.target.matches('.quantity-btn') || e.target.closest('.quantity-btn')) {
        e.preventDefault();
        const button = e.target.closest('.quantity-btn') || e.target;
        this.handleQuantityChange(button);
      } else if (e.target.closest('.cart-item-remove')) {
        e.preventDefault();
        const removeBtn = e.target.closest('.cart-item-remove');
        const itemKey = removeBtn.dataset.itemKey;
        this.removeItem(itemKey);
      } else if (e.target.matches('.add-to-cart-btn[data-product-id]') || e.target.closest('.add-to-cart-btn[data-product-id]')) {
        e.preventDefault();
        const button = e.target.closest('.add-to-cart-btn[data-product-id]') || e.target;
        this.addRecommendedProduct(button);
      } else if (e.target.matches('#prevBtn') || e.target.closest('#prevBtn')) {
        e.preventDefault();
        this.previousSlide();
      } else if (e.target.matches('#nextBtn') || e.target.closest('#nextBtn')) {
        e.preventDefault();
        this.nextSlide();
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target.matches('.quantity-input')) {
        this.handleQuantityInputChange(e.target);
      } else if (e.target.matches('.variant-selector[data-product-id]')) {
        this.updateRecommendedProductVariant(e.target);
      }
    });

    document.addEventListener('input', (e) => {
      if (e.target.matches('.quantity-input')) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.handleQuantityInputChange(e.target);
        }, 500);
      }
    });
  }

  static async handleQuantityChange(button) {
    if (this.isUpdating) return;
    try {
      const action = button.dataset.action;
      const controls = button.closest('.quantity-controls');
      const input = controls?.querySelector('.quantity-input');
      const itemKey = controls?.dataset.itemKey;
      if (!input || !itemKey) return;
      let newQuantity = parseInt(input.value) || 1;
      if (action === 'increase') {
        newQuantity++;
      } else if (action === 'decrease') {
        newQuantity = Math.max(0, newQuantity - 1);
      }
      await this.updateCartItem(itemKey, newQuantity);
    } catch (error) {
      this.showError('Failed to update quantity. Please try again.');
    }
  }

  static async handleQuantityInputChange(input) {
    if (this.isUpdating) return;
    try {
      const itemKey = input.dataset.itemKey;
      const newQuantity = Math.max(0, parseInt(input.value) || 0);
      const originalQuantity = parseInt(input.dataset.originalValue) || 1;
      if (newQuantity !== originalQuantity && itemKey) {
        await this.updateCartItem(itemKey, newQuantity);
      }
    } catch (error) {
      this.showError('Failed to update quantity. Please try again.');
    }
  }

  static async updateCartItem(itemKey, quantity) {
    if (!itemKey || this.isUpdating) return;
    const cartItem = document.querySelector('[data-key="' + itemKey + '"]');
    if (!cartItem) return;
    this.isUpdating = true;
    cartItem.classList.add('updating');
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: quantity })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'HTTP ' + response.status + ': ' + response.statusText);
      }
      const cartData = await response.json();
      await this.refreshCartDrawer();
    } catch (error) {
      const input = cartItem.querySelector('.quantity-input');
      if (input) input.value = input.dataset.originalValue;
      this.showError('Failed to update cart. Please try again.');
    } finally {
      this.isUpdating = false;
      cartItem.classList.remove('updating');
    }
  }

  static async removeItem(itemKey) {
    try {
      await this.updateCartItem(itemKey, 0);
    } catch (error) {
      this.showError('Failed to remove item. Please try again.');
    }
  }

  static async addRecommendedProduct(button) {
    if (button.disabled) return;
    try {
      const productId = button.dataset.productId;
      const slideItem = button.closest('.slide-item');
      const selectedVariantInput = slideItem?.querySelector('.selected-variant-id');
      let variantId;
      if (selectedVariantInput) {
        variantId = selectedVariantInput.value;
      } else {
        const variantSelector = slideItem?.querySelector('.variant-selector');
        variantId = variantSelector?.value;
      }
      if (!variantId) throw new Error('No variant ID found');
      const originalText = button.textContent;
      const originalBackground = button.style.background;
      button.classList.add('adding');
      button.textContent = 'Adding...';
      button.disabled = true;
      button.style.background = '#6b7280';
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.description || 'HTTP ' + response.status);
      }
      const responseData = await response.json();
      button.textContent = 'Added!';
      button.style.background = '#40626F';
      setTimeout(async () => {
        try { await this.refreshCartDrawer(); }
        catch (refreshError) { window.location.reload(); }
      }, 500);
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = originalBackground;
        button.classList.remove('adding');
        button.disabled = false;
      }, 2000);
    } catch (error) {
      button.textContent = 'ERROR';
      button.style.background = '#ef4444';
      setTimeout(() => {
        button.textContent = button.textContent.includes('SOLD OUT') ? 'SOLD OUT' : 'Add';
        button.style.background = '';
        button.classList.remove('adding');
        button.disabled = button.textContent.includes('SOLD OUT');
      }, 3000);
      this.showError('Failed to add product. Please try again.');
    }
  }

  static async fetchCartData() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    if (!response.ok) throw new Error('Failed to fetch cart data: ' + response.status);
    return response.json();
  }

  static async refreshCartDrawer() {
    try {
      const cartData = await this.fetchCartData();
      const cartCountElement = document.getElementById('cartDrawerCount');
      if (cartCountElement) cartCountElement.textContent = '(' + cartData.item_count + ')';
      document.querySelectorAll('[data-cart-count]').forEach(element => {
        element.textContent = cartData.item_count;
      });
      await this.rebuildCartContent(cartData);
      this.updateCartFooter(cartData);
      this.updateShippingProgressFromData(cartData);
      this.initSlider();
      this.initVariantHandlers();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: cartData.item_count } }));
    } catch (error) {
      setTimeout(() => {
        if (confirm('Cart update failed. Would you like to reload the page?')) {
          window.location.reload();
        }
      }, 1000);
    }
  }


  static async rebuildCartContent(cartData) {
    if (typeof window.BOLD !== 'undefined' && typeof window.BOLD.common !== 'undefined' && typeof window.BOLD.common.cartDoctor !== 'undefined') {
      cartData = window.BOLD.common.cartDoctor.fix(cartData);
    }
    const cartItemsContainer = document.getElementById('cartItemsContainer');
    if (!cartItemsContainer) return;
    if (cartData.item_count === 0) {
      const existingCartItems = cartItemsContainer.querySelector('.cart-items');
      if (existingCartItems) {
        existingCartItems.innerHTML = '<div class="cart-empty text-center py-[60px] px-5 text-[#6b7280]" role="status" aria-live="polite"><h3 class="m-0 mb-2 text-[#111827] text-lg font-semibold">Your cart is empty</h3><p class="text-sm leading-[1.5] m-0">Start shopping to add items to your cart.</p></div>';
      }
      return;
    }
    let cartItemsHTML = '';
    cartData.items.forEach((item, index) => {
      const lineNumber = index + 1;
      const itemKey = this.escapeHtml(item.key);
      const productTitle = this.escapeHtml(item.product_title);
      const productUrl = this.escapeHtml(item.url);
      let variantHTML = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        const variantOptions = item.variant_title.split(' / ');
        if (variantOptions.length >= 1) {
          variantHTML += '<p class="cart-item-variant text-sm text-[#232428] m-0 mb-0.5 leading-[1.3] font-body">Color: ' + this.escapeHtml(variantOptions[0]) + '</p>';
        }
        if (variantOptions.length >= 2) {
          variantHTML += '<p class="cart-item-variant text-sm text-[#232428] m-0 mb-0.5 leading-[1.3] font-body">Size: ' + this.escapeHtml(variantOptions[1]) + '</p>';
        }
      }
      let saleBadgeHTML = '';
      if (item.original_line_price > item.final_line_price) {
        saleBadgeHTML = '<div class="cart-item-sale-badge hidden absolute -top-[25px] left-0 text-[11px] font-bold uppercase tracking-[0.5px] m-0 py-[3px] px-[6px] rounded-none inline-block leading-[1.2] bg-[#ef4444] text-white" aria-label="On sale">SALE</div>';
      }
      let stockHTML = '';
      if (window.cartItemInventory) {
        const invData = window.cartItemInventory[item.variant_id];
        if (invData && invData.management === 'shopify') {
          if (invData.quantity <= 0) {
            if (invData.policy === 'deny') {
              stockHTML = '<div class="cart-item-stock-badge absolute -top-[25px] right-0 text-sm font-normal m-0 py-[2px] px-[6px] rounded-[2px] inline-block leading-[1.3] bg-[#40626F] text-white normal-case tracking-normal" aria-label="Out of stock">OUT OF STOCK</div>';
            }
          } else if (invData.quantity <= 9) {
            stockHTML = '<div class="cart-item-stock-badge absolute -top-[25px] right-0 text-sm font-normal m-0 py-[2px] px-[6px] rounded-[2px] inline-block leading-[1.3] bg-[#40626F] text-white normal-case tracking-normal" aria-label="' + invData.quantity + ' items left">' + invData.quantity + ' LEFT</div>';
          }
        }
      }
      let imageHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f9fafb; color: #9ca3af; font-size: 12px;">No Image</div>';
      if (item.image) {
        imageHTML = '<img src="' + this.escapeHtml(item.image) + '" alt="' + productTitle + '" loading="lazy" width="80" height="80" class="w-full h-full object-cover">';
      }
      const currentPrice = this.formatCurrency(item.final_line_price);
      let priceHTML = '<p class="cart-item-current-price text-xl font-normal text-[#232428] m-0 leading-[1.2]">' + currentPrice + '</p>';
      if (item.original_line_price !== item.final_line_price) {
        const originalPrice = this.formatCurrency(item.original_line_price);
        priceHTML += '<p class="cart-item-original-price text-sm text-black/50 line-through m-0 leading-[1.3]" aria-label="Original price">' + originalPrice + '</p>';
      }
      cartItemsHTML += '<article class="cart-item flex items-start gap-[15px] py-5 border-b border-[#f3f4f6] relative last:border-b-0" data-key="' + itemKey + '" data-line="' + lineNumber + '" role="listitem" aria-labelledby="item-title-' + lineNumber + '"><div class="cart-item-image w-[176px] h-[126px] shrink-0 rounded-none overflow-hidden bg-[#f9fafb] border border-[#F5F5F5] cursor-pointer transition-opacity duration-200 hover:opacity-80" data-href="' + productUrl + '">' + imageHTML + '</div><div class="cart-item-details flex-1 flex flex-col min-w-0"><div class="cart-item-info mb-3"><h3 class="cart-item-title text-base font-extrabold text-[#232428] m-0 mb-1 leading-[1.1] break-words cursor-pointer transition-colors duration-200 font-title hover:text-[#232428] hover:underline" id="item-title-' + lineNumber + '"><a href="' + productUrl + '" style="display: block; color: inherit;">' + productTitle + '</a></h3>' + variantHTML + '</div><div class="cart-item-controls flex justify-between items-end gap-3 relative">' + saleBadgeHTML + stockHTML + '<div class="quantity-controls flex items-center border border-[#999999] rounded-l-[2px] rounded-r-[2px] bg-white overflow-hidden" data-item-key="' + itemKey + '" role="group" aria-label="Quantity controls for ' + productTitle + '"><button class="quantity-btn quantity-decrease bg-transparent w-[30px] h-[34px] flex items-center justify-center cursor-pointer text-[#232428] shrink-0 transition-all duration-200 hover:bg-[#f5f5f5] hover:text-[#232428] disabled:opacity-40 disabled:cursor-not-allowed rounded-l-[2px] border-r border-[#999999]" type="button" data-action="decrease" aria-label="Decrease quantity"><svg width="12" height="2" viewBox="0 0 12 2" fill="none" aria-hidden="true"><path d="M1 1H11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button><input type="number" class="quantity-input border-none w-[41px] text-center text-sm font-normal p-0 bg-transparent text-[#232428] focus:outline-none [appearance:textfield] [-webkit-appearance:none] [-moz-appearance:textfield]" value="' + item.quantity + '" min="0" max="99" data-original-value="' + item.quantity + '" data-item-key="' + itemKey + '" aria-label="Quantity for ' + productTitle + '"><button class="quantity-btn quantity-increase bg-transparent w-[31px] h-[34px] flex items-center justify-center cursor-pointer text-[#232428] shrink-0 transition-all duration-200 hover:bg-[#f5f5f5] hover:text-[#232428] disabled:opacity-40 disabled:cursor-not-allowed rounded-r-[2px] border-l border-[#999999]" type="button" data-action="increase" aria-label="Increase quantity"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 1V11M1 6H11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button></div><div class="cart-item-price text-right shrink-0 flex flex-row-reverse gap-2 items-center" aria-label="Price for ' + productTitle + '">' + priceHTML + '</div></div></div><button class="cart-item-remove absolute top-4 right-0 bg-transparent border-none cursor-pointer text-[#232428] p-0 rounded-none transition-all duration-200 w-6 h-6 flex items-center justify-center hover:text-[#232428] hover:opacity-70 focus:outline-2 focus:outline-offset-2 focus:outline-[#3b82f6]" type="button" data-item-key="' + itemKey + '" aria-label="Remove ' + productTitle + ' from cart"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M8 3.5c-.348 0-.707.116-.959.352-.251.236-.374.572-.374.898v.625H3v1.25h.667v10c0 1.028.903 1.875 2 1.875h8c1.097 0 2-.847 2-1.875v-10H16.333v-1.25h-3.666v-.625c0-.326-.123-.663-.375-.899-.252-.235-.61-.351-.959-.351H8zm0 1.25h2.667v.625H8V4.75zM4.667 6.625h9.333v10c0 .347-.297.625-.667.625H5.667c-.37 0-.667-.278-.667-.625l-.333-10zm1.333 1.875v6.875h1.333V8.5H6zm2.667 0v6.875H10V8.5H8.667zm2.666 0v6.875h1.334V8.5h-1.334z" fill="#232428"/></svg></button></article>';
    });
    const existingCartItems = cartItemsContainer.querySelector('.cart-items');
    if (existingCartItems) {
      existingCartItems.innerHTML = cartItemsHTML;
      existingCartItems.setAttribute('role', 'list');
      existingCartItems.setAttribute('aria-label', 'Items in cart');
    } else {
      cartItemsContainer.innerHTML = '<div class="cart-items grow" id="cartItems" role="list" aria-label="Items in cart">' + cartItemsHTML + '</div>';
    }
  }

  static updateCartFooter(cartData) {
    const existingSubtotal = document.getElementById('cartSubtotal');
    if (existingSubtotal) {
      existingSubtotal.textContent = this.formatCurrency(cartData.total_price);
    }
  }

  static initVariantHandlers() {
    document.querySelectorAll('.slide-item').forEach(slideItem => {
      const productId = slideItem.dataset.productId;
      if (productId) {
        this.updateVariantSelections(productId);
      }
    });
  }

  static updateRecommendedProductVariant(changedSelector) {
    const slideItem = changedSelector.closest('.slide-item');
    const productId = changedSelector.dataset.productId;
    if (!productId || !slideItem) return;
    this.updateVariantSelections(productId);
  }

  static updateVariantSelections(productId) {
    const slideItem = document.querySelector('.slide-item[data-product-id="' + productId + '"]');
      if (!slideItem) return;
      const colorSelector = slideItem.querySelector('.color-selector');
      const sizeSelector = slideItem.querySelector('.size-selector');
      const variantIdInput = slideItem.querySelector('.selected-variant-id');
      const priceContainer = slideItem.querySelector('.slide-product-price .current-price');
      const productImage = slideItem.querySelector('.slide-product-image img');
      const variants = JSON.parse(slideItem.dataset.productVariants);
      if (!Array.isArray(variants)) return;
      const selectedColor = colorSelector?.value || null;
      const selectedSize = sizeSelector?.value || null;
      const matchingVariant = variants.find(variant => {
        if (!variant.available) return false;
        const variantOptions = variant.title.split(' / ');
        const colorMatch = !selectedColor || variantOptions.includes(selectedColor);
        const sizeMatch = !selectedSize || variantOptions.includes(selectedSize);
        return colorMatch && sizeMatch;
      });
      if (matchingVariant) {
        if (variantIdInput) variantIdInput.value = matchingVariant.id;
        if (priceContainer) priceContainer.textContent = this.formatCurrency(matchingVariant.price);
        const originalPriceContainer = slideItem.querySelector('.slide-product-price .original-price');
        if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
          if (!originalPriceContainer) {
            const originalPriceSpan = document.createElement('span');
            originalPriceSpan.className = 'original-price';
            originalPriceSpan.setAttribute('aria-label', 'Original price');
            slideItem.querySelector('.slide-product-price').appendChild(originalPriceSpan);
          }
          slideItem.querySelector('.slide-product-price .original-price').textContent = this.formatCurrency(matchingVariant.compare_at_price);
        } else if (originalPriceContainer) {
          originalPriceContainer.remove();
        }
        if (productImage && matchingVariant.featured_image) {
          productImage.src = matchingVariant.featured_image.src;
          productImage.alt = matchingVariant.featured_image.alt || matchingVariant.title;
        }
      }
  }

  static initSlider() {
    const slides = document.querySelectorAll('.slide-item');
    if (slides.length === 0) {
      this.currentSlide = 0;
    } else if (this.currentSlide >= slides.length) {
      this.currentSlide = slides.length - 1;
    }
    this.updateSliderPosition();
    this.updateSliderButtons();
  }

  static previousSlide() {
    const slides = document.querySelectorAll('.slide-item');
    if (slides.length === 0) return;
    if (this.currentSlide > 0) {
      this.currentSlide--;
      this.updateSliderPosition();
      this.updateSliderButtons();
    }
  }

  static nextSlide() {
    const slides = document.querySelectorAll('.slide-item');
    if (slides.length === 0) return;
    if (this.currentSlide < slides.length - 1) {
      this.currentSlide++;
      this.updateSliderPosition();
      this.updateSliderButtons();
    }
  }

  static updateSliderPosition() {
    const track = document.getElementById('sliderTrack');
    if (!track) return;
    const slide = track.querySelector('.slide-item');
    if (!slide) return;
    const slideWidth = slide.offsetWidth;
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    const moveAmount = slideWidth + gap;
    track.style.transform = 'translateX(-' + (this.currentSlide * moveAmount) + 'px)';
  }

  static updateSliderButtons() {
    const slides = document.querySelectorAll('.slide-item');
    const prevBtn = document.getElementById('prevBtn');
    const nextBtn = document.getElementById('nextBtn');
    if (prevBtn) {
      prevBtn.disabled = this.currentSlide === 0;
      prevBtn.setAttribute('aria-disabled', this.currentSlide === 0);
    }
    if (nextBtn) {
      nextBtn.disabled = this.currentSlide >= slides.length - 1;
      nextBtn.setAttribute('aria-disabled', this.currentSlide >= slides.length - 1);
    }
  }

  static formatCurrency(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);
  }

  static escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  static showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'cart-error-notification';
    errorDiv.textContent = message;
    errorDiv.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #ef4444; color: white; padding: 12px 16px; border-radius: 6px; font-size: 14px; z-index: 10000; box-shadow: 0 4px 12px rgba(0,0,0,0.15);';
    document.body.appendChild(errorDiv);
    setTimeout(() => { if (errorDiv.parentNode) errorDiv.remove(); }, 5000);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  CartDrawer.init();
  CartBubbleUpdater.init();
});

// Global functions for theme integration
window.CartDrawer = CartDrawer;
window.openCartDrawer = () => CartDrawer.open();
window.closeCartDrawer = () => CartDrawer.close();
window.toggleCartDrawer = () => CartDrawer.toggle();
